import type { Prettify } from '@repo/shared/types';

/**
 * All possible error codes for LocationError
 */
export type LocationErrorCode =
	| 'PERMISSION_DENIED'
	| 'PROMPT_REQUIRED'
	| 'POSITION_UNAVAILABLE'
	| 'TIMEOUT'
	| 'NOT_SUPPORTED'
	| 'UNKNOWN_ERROR';

/**
 * Custom error class for location-related errors
 */
export class LocationError extends Error {
	constructor(
		public code: LocationErrorCode,
		message: string,
	) {
		super(message);
		this.name = 'LocationError';
		// Ensure prototype chain is intact across targets(e.g ES2015)
		Object.setPrototypeOf(this, new.target.prototype);
	}
}

export interface LocationOptions {
	enableHighAccuracy?: boolean;
	/** Maximum time allowed to retrieve position in milliseconds */
	timeout?: number;
	/** Maximum age of cached position in milliseconds */
	maximumAge?: number;
	/**
	 * If true, do not trigger the browser permission prompt.
	 * Function resolves only when permission is already granted, otherwise rejects with PERMISSION_DENIED.
	 */
	grantedOnly?: boolean;
	/**
	 * Strategy to detect location.
	 * - 'user': use browser geolocation only
	 * - 'ip': use IP-based detection only
	 * - 'ip-fallback': try browser geolocation first; on failure, fall back to IP
	 * @default 'user'
	 */
	detection?: 'user' | 'ip' | 'ip-fallback';
}

/** Address components with main and secondary parts from Google's geocoding response */
export interface FormattedAddress {
	/** Main address line (street number + route) */
	mainText: string;
	/** Secondary address parts (city, state, country) */
	secondaryText?: string;

	/** Full formatted address string */
	formattedAddress: string;
}

export interface Coords {
	lat: number;
	lng: number;
}
/** Location information with coordinates and optional address */
export type MapLocation = Prettify<
	FormattedAddress &
		(
			| ({
					/** Google Places API place ID */
					placeId: string;
			  } & Partial<Coords>)
			| (Coords & { placeId?: string })
		)
>;
